import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { 
  calculateTakeProfitLevels, 
  calculateExpectedValue, 
  calculateRequiredWinRate,
  convertWinRateToOdds
} from '../utils/riskCalculations';

const RiskRewardVisualizer = () => {
  // Form state
  const [entryPrice, setEntryPrice] = useState(100);
  const [stopLossPrice, setStopLossPrice] = useState(95);
  const [direction, setDirection] = useState('long');
  const [winRate, setWinRate] = useState(50);
  const [customTargets, setCustomTargets] = useState(false);
  const [takeProfitPrice1, setTakeProfitPrice1] = useState(110);
  const [takeProfitPrice2, setTakeProfitPrice2] = useState(115);
  const [takeProfitPrice3, setTakeProfitPrice3] = useState(120);
  
  // Results state
  const [takeProfitLevels, setTakeProfitLevels] = useState([]);
  const [expectedValue, setExpectedValue] = useState(null);
  const [error, setError] = useState(null);
  
  // Keep stop loss and direction in sync
  useEffect(() => {
    const isLong = entryPrice > stopLossPrice;
    setDirection(isLong ? 'long' : 'short');
  }, [entryPrice, stopLossPrice]);
  
  // Update default take profit levels when entry changes
  useEffect(() => {
    if (!customTargets) {
      const riskDistance = Math.abs(entryPrice - stopLossPrice);
      if (direction === 'long') {
        setTakeProfitPrice1(entryPrice + riskDistance);
        setTakeProfitPrice2(entryPrice + (riskDistance * 2));
        setTakeProfitPrice3(entryPrice + (riskDistance * 3));
      } else {
        setTakeProfitPrice1(entryPrice - riskDistance);
        setTakeProfitPrice2(entryPrice - (riskDistance * 2));
        setTakeProfitPrice3(entryPrice - (riskDistance * 3));
      }
    }
  }, [entryPrice, stopLossPrice, direction, customTargets]);
  
  // Calculate risk/reward metrics
  const calculateRiskReward = () => {
    try {
      setError(null);
      
      if (!entryPrice || entryPrice <= 0) {
        throw new Error("Entry price must be positive");
      }
      
      if (!stopLossPrice || stopLossPrice <= 0) {
        throw new Error("Stop loss price must be positive");
      }
      
      if (entryPrice === stopLossPrice) {
        throw new Error("Entry and stop loss prices cannot be the same");
      }
      
      if (direction === 'long' && stopLossPrice >= entryPrice) {
        throw new Error("For long positions, stop loss must be below entry price");
      } else if (direction === 'short' && stopLossPrice <= entryPrice) {
        throw new Error("For short positions, stop loss must be above entry price");
      }
      
      // Get take profit levels based on risk-reward ratios
      const isLong = direction === 'long';
      let targetPrices = [];
      
      if (customTargets) {
        // Custom targets validation
        if (!takeProfitPrice1 || takeProfitPrice1 <= 0 || 
            !takeProfitPrice2 || takeProfitPrice2 <= 0 || 
            !takeProfitPrice3 || takeProfitPrice3 <= 0) {
          throw new Error("All take profit prices must be positive");
        }
        
        // Check targets are on the correct side of entry
        if ((isLong && takeProfitPrice1 <= entryPrice) || 
            (!isLong && takeProfitPrice1 >= entryPrice) ||
            (isLong && takeProfitPrice2 <= entryPrice) || 
            (!isLong && takeProfitPrice2 >= entryPrice) ||
            (isLong && takeProfitPrice3 <= entryPrice) || 
            (!isLong && takeProfitPrice3 >= entryPrice)) {
          throw new Error(`For ${isLong ? 'long' : 'short'} positions, take profit must be ${isLong ? 'above' : 'below'} entry price`);
        }
        
        // Calculate ratios for custom targets
        const riskDistance = Math.abs(entryPrice - stopLossPrice);
        
        // Calculate R-multiple for each target
        const ratio1 = Math.abs(takeProfitPrice1 - entryPrice) / riskDistance;
        const ratio2 = Math.abs(takeProfitPrice2 - entryPrice) / riskDistance;
        const ratio3 = Math.abs(takeProfitPrice3 - entryPrice) / riskDistance;
        
        targetPrices = [
          { price: takeProfitPrice1, ratio: ratio1 },
          { price: takeProfitPrice2, ratio: ratio2 },
          { price: takeProfitPrice3, ratio: ratio3 }
        ];
      } else {
        // Standard 1R, 2R, 3R targets
        const targets = calculateTakeProfitLevels(entryPrice, stopLossPrice, [1, 2, 3]);
        targetPrices = targets.map(tp => ({ price: tp.takeProfitPrice, ratio: tp.ratio }));
      }
      
      // Calculate take profit levels with details
      const levels = targetPrices.map(target => {
        const ratio = target.ratio;
        
        // Get distance from entry
        const distance = Math.abs(target.price - entryPrice);
        const distancePercentage = (distance / entryPrice) * 100;
        
        // Calculate required win rate for this R/R to be profitable
        const requiredWinRate = calculateRequiredWinRate(ratio);
        
        // Calculate expected value at current win rate
        const ev = calculateExpectedValue(winRate, ratio);
        
        return {
          takeProfitPrice: target.price,
          ratio,
          distance,
          distancePercentage,
          requiredWinRate,
          expectedValue: ev.expectedValue,
          isPositiveExpectancy: ev.isPositiveExpectancy
        };
      });
      
      setTakeProfitLevels(levels);
      
      // Calculate expected value for average R/R ratio
      const avgRatio = targetPrices.reduce((sum, tp) => sum + tp.ratio, 0) / targetPrices.length;
      setExpectedValue(calculateExpectedValue(winRate, avgRatio));
      
    } catch (err) {
      setError(err.message);
      setTakeProfitLevels([]);
      setExpectedValue(null);
    }
  };
  
  // Reset form to defaults
  const handleReset = () => {
    setEntryPrice(100);
    setStopLossPrice(95);
    setDirection('long');
    setWinRate(50);
    setCustomTargets(false);
    setTakeProfitPrice1(110);
    setTakeProfitPrice2(115);
    setTakeProfitPrice3(120);
    setTakeProfitLevels([]);
    setExpectedValue(null);
    setError(null);
  };

  return (
    <Card className="shadow-sm mb-4">
      <Card.Header className="card-header-gradient">
        <h4 className="mb-0">
          <i className="bi bi-bar-chart me-2"></i>
          Risk/Reward Visualizer
        </h4>
      </Card.Header>
      <Card.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Entry Price</Form.Label>
                <div className="input-group">
                  <span className="input-group-text">$</span>
                  <Form.Control
                    type="number"
                    min="0.0001"
                    step="0.01"
                    value={entryPrice}
                    onChange={(e) => setEntryPrice(parseFloat(e.target.value))}
                  />
                </div>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Stop Loss Price
                  <span className={`ms-2 badge ${direction === 'long' ? 'bg-danger' : 'bg-success'}`}>
                    {direction === 'long' ? 'Below Entry' : 'Above Entry'}
                  </span>
                </Form.Label>
                <div className="input-group">
                  <span className="input-group-text">$</span>
                  <Form.Control
                    type="number"
                    min="0.0001"
                    step="0.01"
                    value={stopLossPrice}
                    onChange={(e) => setStopLossPrice(parseFloat(e.target.value))}
                    className={stopLossPrice === entryPrice ? 'is-invalid' : ''}
                  />
                </div>
                <Form.Text className={`text-${direction === 'long' ? 'danger' : 'success'}`}>
                  {direction === 'long' 
                    ? 'Stop Loss must be below Entry Price' 
                    : 'Stop Loss must be above Entry Price'}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Expected Win Rate</Form.Label>
                <div className="input-group">
                  <Form.Control
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={winRate}
                    onChange={(e) => setWinRate(parseFloat(e.target.value))}
                  />
                  <span className="input-group-text">%</span>
                </div>
                <Form.Text className="text-muted">
                  Your historical win rate for this type of trade
                </Form.Text>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Take Profit Targets</Form.Label>
                <div className="d-flex">
                  <Form.Check
                    type="radio"
                    id="targets-standard"
                    label="Standard (1R, 2R, 3R)"
                    checked={!customTargets}
                    onChange={() => setCustomTargets(false)}
                    className="me-3"
                  />
                  <Form.Check
                    type="radio"
                    id="targets-custom"
                    label="Custom Targets"
                    checked={customTargets}
                    onChange={() => setCustomTargets(true)}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          
          {customTargets && (
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Target 1</Form.Label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <Form.Control
                      type="number"
                      min="0.0001"
                      step="0.01"
                      value={takeProfitPrice1}
                      onChange={(e) => setTakeProfitPrice1(parseFloat(e.target.value))}
                    />
                  </div>
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Target 2</Form.Label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <Form.Control
                      type="number"
                      min="0.0001"
                      step="0.01"
                      value={takeProfitPrice2}
                      onChange={(e) => setTakeProfitPrice2(parseFloat(e.target.value))}
                    />
                  </div>
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Target 3</Form.Label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <Form.Control
                      type="number"
                      min="0.0001"
                      step="0.01"
                      value={takeProfitPrice3}
                      onChange={(e) => setTakeProfitPrice3(parseFloat(e.target.value))}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>
          )}
          
          <div className="d-flex mt-4">
            <Button variant="primary" onClick={calculateRiskReward} className="me-2">
              <i className="bi bi-calculator me-2"></i>
              Calculate
            </Button>
            <Button variant="outline-secondary" onClick={handleReset}>
              <i className="bi bi-arrow-repeat me-1"></i>
              Reset
            </Button>
          </div>
        </Form>
        
        {takeProfitLevels.length > 0 && (
          <div className="mt-4">
            <h5 className="mb-3">
              <span className="gradient-text">Risk/Reward Analysis</span>
            </h5>
            
            <div className="price-chart p-3 mb-4" style={{ backgroundColor: 'rgba(25, 22, 50, 0.8)', borderRadius: '8px' }}>
              <h6 className="mb-3">Price Levels Visualization</h6>
              
              <div className="price-levels position-relative" style={{ height: '200px', overflow: 'hidden' }}>
                {/* Visual price bar */}
                <div className="position-absolute w-100 h-100">
                  {/* Calculate price range and positions */}
                  {(() => {
                    const prices = [
                      stopLossPrice,
                      entryPrice,
                      ...takeProfitLevels.map(level => level.takeProfitPrice)
                    ];
                    
                    const minPrice = Math.min(...prices) * 0.98;
                    const maxPrice = Math.max(...prices) * 1.02;
                    const priceRange = maxPrice - minPrice;
                    
                    // Function to convert price to Y position (inverted, higher price = lower Y)
                    const getPricePosition = (price) => {
                      return ((maxPrice - price) / priceRange) * 100;
                    };
                    
                    return (
                      <>
                        {/* Stop Loss Line */}
                        <div 
                          className="position-absolute w-100" 
                          style={{ 
                            top: `${getPricePosition(stopLossPrice)}%`, 
                            height: '2px', 
                            backgroundColor: '#dc3545',
                            zIndex: 1
                          }}
                        />
                        <div 
                          className="position-absolute px-2 py-1 rounded" 
                          style={{ 
                            top: `${getPricePosition(stopLossPrice) - 2}%`, 
                            right: '10px',
                            backgroundColor: 'rgba(220, 53, 69, 0.8)',
                            color: 'white',
                            fontSize: '0.8rem',
                            zIndex: 2
                          }}
                        >
                          SL: ${stopLossPrice.toFixed(2)}
                        </div>
                        
                        {/* Entry Line */}
                        <div 
                          className="position-absolute w-100" 
                          style={{ 
                            top: `${getPricePosition(entryPrice)}%`, 
                            height: '2px', 
                            backgroundColor: 'white',
                            zIndex: 1
                          }}
                        />
                        <div 
                          className="position-absolute px-2 py-1 rounded" 
                          style={{ 
                            top: `${getPricePosition(entryPrice) - 2}%`, 
                            left: '10px',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            color: '#121025',
                            fontWeight: 'bold',
                            fontSize: '0.8rem',
                            zIndex: 2
                          }}
                        >
                          Entry: ${entryPrice.toFixed(2)}
                        </div>
                        
                        {/* Take Profit Lines */}
                        {takeProfitLevels.map((level, index) => (
                          <React.Fragment key={index}>
                            <div 
                              className="position-absolute w-100" 
                              style={{ 
                                top: `${getPricePosition(level.takeProfitPrice)}%`, 
                                height: '2px', 
                                backgroundColor: '#00f260',
                                zIndex: 1
                              }}
                            />
                            <div 
                              className="position-absolute px-2 py-1 rounded" 
                              style={{ 
                                top: `${getPricePosition(level.takeProfitPrice) - 2}%`, 
                                right: '10px',
                                backgroundColor: 'rgba(0, 242, 96, 0.8)',
                                color: 'black',
                                fontSize: '0.8rem',
                                zIndex: 2
                              }}
                            >
                              TP{index + 1}: ${level.takeProfitPrice.toFixed(2)} ({level.ratio.toFixed(1)}R)
                            </div>
                          </React.Fragment>
                        ))}
                        
                        {/* Risk Zone Shading */}
                        <div 
                          className="position-absolute w-100" 
                          style={{ 
                            top: direction === 'long' ? `${getPricePosition(entryPrice)}%` : '0', 
                            bottom: direction === 'long' ? '0' : `${100 - getPricePosition(entryPrice)}%`, 
                            backgroundColor: 'rgba(220, 53, 69, 0.1)',
                            borderLeft: '4px solid rgba(220, 53, 69, 0.5)',
                            zIndex: 0
                          }}
                        />
                        
                        {/* Reward Zone Shading */}
                        <div 
                          className="position-absolute w-100" 
                          style={{ 
                            top: direction === 'long' ? '0' : `${getPricePosition(entryPrice)}%`, 
                            bottom: direction === 'long' ? `${100 - getPricePosition(entryPrice)}%` : '0', 
                            backgroundColor: 'rgba(0, 242, 96, 0.1)',
                            borderLeft: '4px solid rgba(0, 242, 96, 0.5)',
                            zIndex: 0
                          }}
                        />
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
            
            <div className="row">
              <div className="col-md-6 mb-4">
                <div className="card h-100">
                  <div className="card-header">
                    <h6 className="mb-0">Take Profit Levels</h6>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table mb-0">
                        <thead>
                          <tr>
                            <th>Target</th>
                            <th>Price</th>
                            <th>Ratio</th>
                            <th>Distance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {takeProfitLevels.map((level, index) => (
                            <tr key={index}>
                              <td>TP{index + 1}</td>
                              <td>${level.takeProfitPrice.toFixed(2)}</td>
                              <td>{level.ratio.toFixed(1)}R</td>
                              <td>{level.distancePercentage.toFixed(2)}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-6 mb-4">
                <div className="card h-100">
                  <div className="card-header">
                    <h6 className="mb-0">Win Rate Analysis</h6>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table mb-0">
                        <thead>
                          <tr>
                            <th>Target</th>
                            <th>Min Win%</th>
                            <th>Expected</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {takeProfitLevels.map((level, index) => (
                            <tr key={index}>
                              <td>TP{index + 1}</td>
                              <td>{level.requiredWinRate.toFixed(1)}%</td>
                              <td>{level.expectedValue.toFixed(2)}R</td>
                              <td>
                                <span className={`badge ${level.isPositiveExpectancy ? 'signal-badge-long' : 'signal-badge-short'}`}>
                                  {level.isPositiveExpectancy ? 'Profitable' : 'Unprofitable'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {expectedValue && (
              <div className="row">
                <div className="col-12">
                  <div className="card mb-4">
                    <div className="card-header">
                      <h6 className="mb-0">Overall Trade Assessment</h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <h6>Risk Details</h6>
                            <div className="d-flex justify-content-between border-bottom pb-2 mb-2">
                              <span>Direction:</span>
                              <span className="fw-bold">{direction.toUpperCase()}</span>
                            </div>
                            <div className="d-flex justify-content-between border-bottom pb-2 mb-2">
                              <span>Entry Price:</span>
                              <span className="fw-bold">${entryPrice.toFixed(2)}</span>
                            </div>
                            <div className="d-flex justify-content-between border-bottom pb-2 mb-2">
                              <span>Stop Loss:</span>
                              <span className="fw-bold">${stopLossPrice.toFixed(2)}</span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span>Risk Per Unit:</span>
                              <span className="fw-bold">${Math.abs(entryPrice - stopLossPrice).toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="col-md-6">
                          <div className="mb-3">
                            <h6>Expected Value</h6>
                            <div className="d-flex justify-content-between border-bottom pb-2 mb-2">
                              <span>Win Rate:</span>
                              <span className="fw-bold">{winRate}%</span>
                            </div>
                            <div className="d-flex justify-content-between border-bottom pb-2 mb-2">
                              <span>Equivalent Odds:</span>
                              <span className="fw-bold">{convertWinRateToOdds(winRate)}</span>
                            </div>
                            <div className="d-flex justify-content-between border-bottom pb-2 mb-2">
                              <span>Average R-Multiple:</span>
                              <span className="fw-bold">{expectedValue.profitFactor.toFixed(2)}</span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span>Expected Value:</span>
                              <span className={`fw-bold ${expectedValue.isPositiveExpectancy ? 'text-success' : 'text-danger'}`}>
                                {expectedValue.expectedValueFormatted}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-3">
                        {expectedValue.isPositiveExpectancy ? (
                          <Alert variant="success">
                            <h6 className="alert-heading mb-2">
                              <i className="bi bi-check-circle me-2"></i>
                              Positive Expected Value
                            </h6>
                            <p className="mb-0">
                              This trade has a positive expected value of {expectedValue.expectedValueFormatted}, 
                              meaning over time, it's expected to be profitable. Your win rate of {winRate}% exceeds 
                              the minimum required win rate of {expectedValue.breakEvenWinRatePercentage.toFixed(1)}%.
                            </p>
                          </Alert>
                        ) : (
                          <Alert variant="danger">
                            <h6 className="alert-heading mb-2">
                              <i className="bi bi-exclamation-triangle me-2"></i>
                              Negative Expected Value
                            </h6>
                            <p className="mb-0">
                              This trade has a negative expected value of {expectedValue.expectedValueFormatted}, 
                              meaning it's expected to lose money over time. To make this trade profitable, 
                              you need a win rate of at least {expectedValue.breakEvenWinRatePercentage.toFixed(1)}%.
                            </p>
                          </Alert>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default RiskRewardVisualizer;